<template>
  <div>
    <v-dialog
      v-model="isDialogOpen"
      max-width="1320"
    >
      <template v-slot:activator="{ props }">
        <v-btn
          color="primary"
          variant="outlined"
          v-bind="props"
        >
          サービスの編集
        </v-btn>
      </template>

      <v-card class="pa-4">
        <page-title value="園提供サービス 編集" />
        <div class="my-4">
          <v-table>
            <thead>
              <tr>
                <th class="text-left">サービス名</th>
                <th class="text-left">請求条件</th>
                <th class="text-left">支払い月</th>
                <th class="text-left">単価</th>
                <th class="text-left">上限</th>
                <th class="text-left">勘定科目</th>
                <th class="text-left">表示/非表示</th>
                <th class="text-right"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="service in services"
                :key="service.uniqueServiceId"
              >
                <td>{{ service.uniqueServiceName }}</td>
                <td>{{ getServiceTypeString(service.serviceType) }}</td>
                <td>{{ getServicePaymentMonthString(service.paymentMonth) }}</td>
                <td>{{ service.price }}</td>
                <td>{{ service.maximumPrice }}</td>
                <td>{{ (service.accountType === 'food' ? '主食・副食費' : 'その他') }}</td>
                <td class="align-center">
                  <v-chip
                    v-if="service.isHidden === 1"
                    variant="flat"
                  >非表示</v-chip>
                  <v-chip
                    v-else-if="service.isHidden === 0"
                    color="green"
                    text-color="white"
                    style="width:4.5em; justify-content: center;"
                    variant="flat"
                  >表示</v-chip>
                </td>
                <td class="d-flex align-center">
                  <v-btn
                    color="primary"
                    class="mx-2"
                    variant="outlined"
                    size="small"
                    @click="setEditingService(service)"
                  >編集</v-btn>
                  <v-btn
                    v-if="!service.isInuse"
                    color="error"
                    class="mx-2"
                    variant="flat"
                    size="small"
                    @click="setDeletingService(service)"
                  >削除</v-btn>
                </td>
              </tr>
            </tbody>
          </v-table>
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isEditDialogOpen"
      max-width="720"
    >
      <v-card
        v-if="editingService"
        class="pa-4"
      >
        <page-title value="サービスの編集" />
        <div class="my-4">
          <v-text-field
            variant="outlined"
            label="サービス名"
            v-model="editingService.uniqueServiceName"
            counter
            maxlength="30"
          ></v-text-field>
          <div style="display: flex; align-items: center;">
            <v-checkbox
              v-model="editingService.isHidden"
              :true-value="1"
              :false-value="0"
              hide-details
            ></v-checkbox>
            <h4 class="my-2 mr-4">非表示対象</h4>
          </div>
        </div>
        <v-progress-linear v-if="isUpdating" class="my-4" indeterminate color="primary" />
        <div class="d-flex align-center">
          <v-spacer />
          <v-btn
            class="mx-2"
            color="gray"
            variant="flat"
            @click="closeEditingService"
          >キャンセル</v-btn>
          <v-btn
            class="mx-2"
            color="primary"
            variant="flat"
            :disabled="!editValid || isUpdating"
            @click="saveEditingService"
          >保存</v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>

    <v-dialog
      v-model="isDeleteDialogOpen"
      max-width="720"
    >
      <v-card
        v-if="deletingService"
        class="pa-4"
      >
        <page-title value="サービスの削除" />
        <div class="my-4">
          <v-table>
            <thead>
              <tr>
                <th class="text-left">サービス名</th>
                <th class="text-left">請求条件</th>
                <th class="text-left">単価</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{{ deletingService.uniqueServiceName }}</td>
                <td>{{ getServiceTypeString(deletingService.serviceType) }}</td>
                <td>{{ deletingService.price }}</td>
              </tr>
            </tbody>
          </v-table>
        </div>
        <div class="my-4">
          <p class="text-error font-weight-bold">本当に削除しますか？この操作は取り消せません。</p>
        </div>
        <v-progress-linear v-if="isDeleting" class="my-4" indeterminate color="error" />
        <div class="d-flex align-center">
          <v-spacer />
          <v-btn
            class="mx-2"
            color="gray"
            variant="flat"
            @click="closeDeletingService"
          >キャンセル</v-btn>
          <v-btn
            class="mx-2"
            color="error"
            variant="flat"
            :disabled="isDeleting"
            @click="deleteService"
          >削除</v-btn>
          <v-spacer />
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';

import PageTitle from '@/components/atoms/PageTitle.vue';
import { schoolServiceModule } from '@/store/dataModules/schoolServiceModule';
import { UniqueServiceMastResponse } from 'chaild-api/lib';

interface DataType {
  isDialogOpen: boolean;
  isUpdating: boolean;
  isDeleting: boolean;
  editingService: UniqueServiceMastResponse | null;
  deletingService: UniqueServiceMastResponse | null;
}

export default defineComponent({
  components: {
    PageTitle,
  },

  data: (): DataType => ({
    isDialogOpen: false,
    isUpdating: false,
    isDeleting: false,
    editingService: null,
    deletingService: null,
  }),

  computed: {
    services() {
      return schoolServiceModule.schoolServices;
    },
    editValid(): boolean {
      return !!(
        this.editingService &&
        this.editingService.uniqueServiceName &&
        this.editingService.uniqueServiceName.trim().length > 0
      );
    },
    // ⭕️ 修正4: 編集ダイアログの v-model 用の算出プロパティ
    isEditDialogOpen: {
      get(): boolean {
        return this.editingService !== null;
      },
      set(val: boolean) {
        if (!val) this.closeEditingService();
      }
    },
    // ⭕️ 修正4: 削除ダイアログの v-model 用の算出プロパティ
    isDeleteDialogOpen: {
      get(): boolean {
        return this.deletingService !== null;
      },
      set(val: boolean) {
        if (!val) this.closeDeletingService();
      }
    }
  },

  methods: {
    getServiceTypeString(serviceType: string) {
      switch (serviceType) {
        case 'perUse': return '回数';   
        case 'monthly': return '固定月額';
        case 'useMonth': return '利用月額';   
        default: return '';
      }
    },
    getServicePaymentMonthString(paymentMonth: number) {
      switch (paymentMonth) {
        case 0: return '同月';   
        case 1: return '翌月';
        case 2: return '翌々月';   
        default: return '';
      }
    },

    // ⭕️ 修正5: Vuexストアの直接変異を防ぐため、オブジェクトをディープコピーする
    setEditingService(service: UniqueServiceMastResponse) {
      this.editingService = JSON.parse(JSON.stringify(service));
    },
    closeEditingService() {
      this.editingService = null;
      schoolServiceModule.listSchoolServices(); // キャンセル時に最新情報に戻す
    },
    async saveEditingService() {
      if (this.editingService) {
        this.isUpdating = true;
        await schoolServiceModule.updateSchoolService(this.editingService);
        await schoolServiceModule.listSchoolServices();
        this.isUpdating = false;
        this.editingService = null;
      }
    },

    setDeletingService(service: UniqueServiceMastResponse) {
      // 削除用はデータを書き換えないのでそのまま参照渡しでもOKだが、安全のためコピー
      this.deletingService = JSON.parse(JSON.stringify(service));
    },
    closeDeletingService() {
      this.deletingService = null;
    },
    async deleteService() {
      if (this.deletingService) {
        this.isDeleting = true;
        await schoolServiceModule.deleteService(this.deletingService);
        await schoolServiceModule.listSchoolServices();
        this.isDeleting = false;
        this.deletingService = null;
      }
    },
  },

  mounted() {
    schoolServiceModule.listSchoolServices();
  }
});
</script>